import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { api, errMsg, tl, waLink } from "@/lib/api";
import { PageTitle } from "@/pages/admin/AdminLayout";

const STATUSES = ["Yeni", "Hazırlanıyor", "Kargoda", "Teslim Edildi", "İptal"];
const PAY = ["Bekliyor", "Ödendi", "Ödendi (TEST)", "İade"];

function OrderRow({ o, onChange }) {
  const [track, setTrack] = useState(o.tracking_no || "");
  const patch = async (body) => {
    try {
      await api.patch(`/admin/orders/${o.id}`, body);
      toast.success("Sipariş güncellendi");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const msg = `Merhaba ${o.name}, ${o.order_no} numaralı siparişiniz: ${o.status}.${o.tracking_no ? ` ${o.cargo} takip no: ${o.tracking_no}` : ""} - onlinesatiyorum.com`;
  return (
    <tr className="border-b align-top" data-testid={`order-row-${o.order_no}`}>
      <td className="p-3"><div className="font-mono font-bold">{o.order_no}</div><div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("tr-TR")}</div></td>
      <td className="p-3 min-w-[180px]"><div className="font-semibold">{o.name}</div><div className="text-xs text-slate-500">{o.phone} · {o.email}</div><div className="text-xs text-slate-500">{o.address} {o.district} / {o.city}</div>{o.note && <div className="text-xs text-amber-700 mt-1">Not: {o.note}</div>}</td>
      <td className="p-3 min-w-[200px] text-xs">{o.items.map((i) => <div key={i.product_id}>{i.qty} x {i.name} ({tl(i.unit_price)})</div>)}</td>
      <td className="p-3 whitespace-nowrap"><div className="font-extrabold text-[#172554]">{tl(o.total)}</div><div className="text-xs text-slate-500">Kargo: {tl(o.shipping)}</div><div className="text-xs">{o.payment_label}</div></td>
      <td className="p-3 space-y-1.5 min-w-[150px]">
        <select data-testid={`order-status-${o.order_no}`} value={o.status} onChange={(e) => patch({ status: e.target.value })} className="h-8 w-full border rounded px-1 text-xs">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        <select data-testid={`order-pay-${o.order_no}`} value={o.payment_status} onChange={(e) => patch({ payment_status: e.target.value })} className="h-8 w-full border rounded px-1 text-xs">{PAY.map((s) => <option key={s}>{s}</option>)}</select>
      </td>
      <td className="p-3 min-w-[170px]">
        <div className="text-xs font-semibold mb-1">{o.cargo}</div>
        <div className="flex gap-1">
          <input data-testid={`order-track-${o.order_no}`} value={track} onChange={(e) => setTrack(e.target.value)} placeholder="Takip no" className="h-8 w-full border rounded px-2 text-xs" />
          <button data-testid={`order-track-save-${o.order_no}`} onClick={() => patch({ tracking_no: track, status: "Kargoda" })} className="h-8 px-2 rounded bg-[#172554] text-white text-xs">Kaydet</button>
        </div>
        <a data-testid={`order-wa-${o.order_no}`} href={waLink(o.phone, msg)} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-[#15803D]"><MessageCircle className="h-3.5 w-3.5" />Müşteriye yaz</a>
      </td>
    </tr>
  );
}

export default function AdminOrders() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-orders"], queryFn: () => api.get("/admin/orders").then((r) => r.data) });
  const [f, setF] = useState("Tümü");
  const list = data.filter((o) => f === "Tümü" || o.status === f);
  return (
    <>
      <PageTitle title="Siparişler" desc="Durum, ödeme ve kargo takibini buradan yönetin." />
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {["Tümü", ...STATUSES].map((s) => (
          <button key={s} data-testid={`orders-filter-${s}`} onClick={() => setF(s)} className={`px-4 h-9 rounded-full text-sm font-semibold whitespace-nowrap border ${f === s ? "bg-[#172554] text-white border-[#172554]" : "bg-white"}`}>
            {s} ({s === "Tümü" ? data.length : data.filter((o) => o.status === s).length})
          </button>
        ))}
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-orders-table">
          <thead className="bg-slate-50 text-xs text-slate-600"><tr>{["Sipariş", "Müşteri", "Ürünler", "Tutar", "Durum / Ödeme", "Kargo"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
          <tbody>{list.map((o) => <OrderRow key={o.id} o={o} onChange={() => qc.invalidateQueries({ queryKey: ["admin-orders"] })} />)}</tbody>
        </table>
        {!list.length && <div className="p-10 text-center text-slate-500">Sipariş yok.</div>}
      </div>
    </>
  );
}
